import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Calendar, User, Package, FileText, CheckCircle,
  Send, Upload, X, Clock, Mail, ChevronLeft, MessageCircle, Image as ImageIcon, Edit, Save, Check, XCircle, Info
} from 'lucide-react';
import axios from 'axios';
import Avatar from './Avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QCIncidentDetail = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [incident, setIncident] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [showPhotos, setShowPhotos] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    incident_date: '',
    project_name: '',
    project_id: '',
    description: '',
    failure_points: [],
    importance: 'Medium',
    photos: []
  });
  const [editUploading, setEditUploading] = useState(false);
  const [newEditFailurePoint, setNewEditFailurePoint] = useState('');
  const [failurePointResponses, setFailurePointResponses] = useState({});
  const [submittingResponse, setSubmittingResponse] = useState({});

  // Check if user is a supplier - handle different role formats
  const isSupplier = user?.role?.toLowerCase() === 'supplier' || 
                     user?.roles?.some(r => r.toLowerCase().includes('supplier'));
  
  // Debug logging
  useEffect(() => {
    console.log('QCIncidentDetail - User:', user);
    console.log('QCIncidentDetail - User Role:', user?.role);
    console.log('QCIncidentDetail - User Roles:', user?.roles);
    console.log('QCIncidentDetail - isSupplier:', isSupplier);
  }, [user, isSupplier]);

  useEffect(() => {
    fetchIncidentData();
    
    // Set up polling to refresh messages every 10 seconds
    const pollInterval = setInterval(() => {
      refreshMessages();
    }, 10000); // 10 seconds
    
    // Cleanup interval on component unmount
    return () => clearInterval(pollInterval);
  }, [id]);

  const fetchIncidentData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      const [incidentRes, messagesRes] = await Promise.all([
        axios.get(`${API}/qc-incidents/${id}`, { headers }),
        axios.get(`${API}/qc-incidents/${id}/messages`, { headers })
      ]);
      
      setIncident(incidentRes.data);
      setMessages(messagesRes.data.messages || []);
      
      // Initialize edit form with incident data, parsing failure_points into array
      const failurePointsArray = incidentRes.data.failure_points 
        ? incidentRes.data.failure_points.split('\n').filter(p => p.trim())
        : [];
      
      setEditForm({
        incident_date: incidentRes.data.incident_date || '',
        project_name: incidentRes.data.project_name || '',
        project_id: incidentRes.data.project_id || '',
        description: incidentRes.data.description || '',
        failure_points: failurePointsArray,
        importance: incidentRes.data.importance || 'Medium',
        photos: incidentRes.data.photos || []
      });
    } catch (error) {
      console.error('Failed to fetch incident:', error);
      alert('Failed to load incident details');
      navigate('/qc-incidents');
    } finally {
      setLoading(false);
    }
  };

  // Refresh messages without showing loading spinner
  const refreshMessages = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const messagesRes = await axios.get(`${API}/qc-incidents/${id}/messages`, { headers });
      setMessages(messagesRes.data.messages || []);
    } catch (error) {
      console.error('Failed to refresh messages:', error);
      // Don't show alert on refresh failure, just log it
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && attachments.length === 0) {
      return;
    }

    setSending(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const messageData = {
        message: newMessage,
        attachments: attachments
      };
      
      const response = await axios.post(
        `${API}/qc-incidents/${id}/messages`,
        messageData,
        { headers }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
      setAttachments([]);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/qc-incidents/upload-image`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      setAttachments([...attachments, ...urls]);
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleMarkAsSolved = async () => {
    if (!window.confirm('Are you sure you want to mark this incident as solved?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/qc-incidents/${id}/solve`, {}, { headers });
      
      alert('Incident marked as solved!');
      fetchIncidentData();
    } catch (error) {
      console.error('Failed to solve incident:', error);
      alert('Failed to mark as solved');
    }
  };

  const handleSendToSupplier = async () => {
    if (!window.confirm('Send email notification to supplier?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/qc-incidents/${id}/send-to-supplier`, {}, { headers });
      
      alert('Email sent to supplier successfully!');
      fetchIncidentData();
    } catch (error) {
      console.error('Failed to send email:', error);
      alert(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleEditImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setEditUploading(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await axios.post(`${API}/qc-incidents/upload-image`, formData, {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      setEditForm(prev => ({
        ...prev,
        photos: [...prev.photos, ...urls]
      }));
    } catch (error) {
      console.error('Image upload error:', error);
      alert('Failed to upload images');
    } finally {
      setEditUploading(false);
      e.target.value = '';
    }
  };

  const removeEditPhoto = (index) => {
    setEditForm(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };
  
  const addEditFailurePoint = () => {
    if (newEditFailurePoint.trim()) {
      setEditForm(prev => ({
        ...prev,
        failure_points: [...prev.failure_points, newEditFailurePoint.trim()]
      }));
      setNewEditFailurePoint('');
    }
  };
  
  const removeEditFailurePoint = (index) => {
    setEditForm(prev => ({
      ...prev,
      failure_points: prev.failure_points.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const updateData = {
        incident_date: editForm.incident_date,
        project_name: editForm.project_name,
        project_id: editForm.project_id,
        description: editForm.description,
        failure_points: Array.isArray(editForm.failure_points)
          ? editForm.failure_points.join('\n')
          : editForm.failure_points,
        importance: editForm.importance,
        photos: editForm.photos
      };
      
      await axios.put(`${API}/qc-incidents/${id}`, updateData, { headers });
      
      alert('Incident updated successfully!');
      setIsEditing(false);
      fetchIncidentData();
    } catch (error) {
      console.error('Failed to update incident:', error);
      alert('Failed to update incident');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewEditFailurePoint('');
    // Reset form to current incident data
    const failurePointsArray = incident.failure_points 
      ? incident.failure_points.split('\n').filter(p => p.trim())
      : [];
    
    setEditForm({
      incident_date: incident.incident_date || '',
      project_name: incident.project_name || '',
      project_id: incident.project_id || '',
      description: incident.description || '',
      failure_points: failurePointsArray,
      importance: incident.importance || 'Medium',
      photos: incident.photos || []
    });
  };

  const handleAddFailurePointResponse = async (failurePointIndex) => {
    const responseText = failurePointResponses[failurePointIndex];
    if (!responseText || !responseText.trim()) {
      alert('Please enter a solution');
      return;
    }

    setSubmittingResponse(prev => ({ ...prev, [failurePointIndex]: true }));

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API}/qc-incidents/${id}/failure-points/${failurePointIndex}/response`,
        { response_text: responseText.trim() },
        { headers }
      );

      // Clear input
      setFailurePointResponses(prev => ({ ...prev, [failurePointIndex]: '' }));
      
      // Refresh incident data
      fetchIncidentData();
    } catch (error) {
      console.error('Failed to add response:', error);
      alert('Failed to add solution');
    } finally {
      setSubmittingResponse(prev => ({ ...prev, [failurePointIndex]: false }));
    }
  };

  const handleUpdateResponseStatus = async (responseId, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API}/qc-incidents/${id}/failure-points/responses/${responseId}/status`,
        { status },
        { headers }
      );

      // Refresh incident data
      fetchIncidentData();
    } catch (error) {
      console.error('Failed to update response status:', error);
      alert('Failed to update status');
    }
  };

  const getResponsesForFailurePoint = (index) => {
    if (!incident.failure_points_responses) return [];
    return incident.failure_points_responses.filter(r => r.failure_point_index === index);
  };

  const calculateAge = (incidentDate) => {
    if (!incidentDate) return 0;
    const incident = new Date(incidentDate);
    const now = new Date();
    const diffTime = Math.abs(now - incident);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'Critical':
        return 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'High':
        return 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800';
      case 'Medium':
        return 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      case 'Low':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-pulse mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading incident...</p>
        </div>
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="p-6">
        <p className="text-red-600">Incident not found</p>
      </div>
    );
  }

  const age = calculateAge(incident.incident_date);
  const isUnsolved = incident.status === 'Unsolved';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/qc-incidents')}
          className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-4"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Incidents
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-orange-600 dark:text-orange-400" />
              {incident.project_name || 'Unnamed Project'}
            </h1>
            {incident.project_id && (
              <p className="text-gray-600 dark:text-gray-400">ID: {incident.project_id}</p>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!isSupplier && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
              >
                <Edit className="w-5 h-5" />
                Edit
              </button>
            )}
            <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getImportanceColor(incident.importance)}`}>
              {incident.importance}
            </span>
            {isUnsolved ? (
              <span className="px-4 py-2 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800">
                Unsolved
              </span>
            ) : (
              <span className="px-4 py-2 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800">
                Solved
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Incident Details */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Incident Details</h2>
              {isEditing && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCancelEdit}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                  >
                    <Save className="w-5 h-5" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
            
            {isEditing ? (
              // Edit Mode
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Project Name</label>
                    <input
                      type="text"
                      value={editForm.project_name}
                      onChange={(e) => setEditForm(prev => ({ ...prev, project_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Project ID</label>
                    <input
                      type="text"
                      value={editForm.project_id}
                      onChange={(e) => setEditForm(prev => ({ ...prev, project_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Incident Date</label>
                  <input
                    type="date"
                    value={editForm.incident_date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, incident_date: e.target.value }))}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Importance Level</label>
                  <select
                    value={editForm.importance}
                    onChange={(e) => setEditForm(prev => ({ ...prev, importance: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Action Required</label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newEditFailurePoint}
                        onChange={(e) => setNewEditFailurePoint(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addEditFailurePoint();
                          }
                        }}
                        placeholder="Add a failure point..."
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={addEditFailurePoint}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
                      >
                        Add
                      </button>
                    </div>
                    
                    {editForm.failure_points.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {editForm.failure_points.map((point, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-600 rounded">
                            <span className="flex-1 text-sm text-gray-900 dark:text-white">{point}</span>
                            <button
                              type="button"
                              onClick={() => removeEditFailurePoint(index)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Photos</label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleEditImageUpload}
                      className="hidden"
                      id="edit-photo-upload"
                      disabled={editUploading}
                    />
                    <label
                      htmlFor="edit-photo-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload className="w-10 h-10 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {editUploading ? 'Uploading...' : 'Click to add more photos'}
                      </span>
                    </label>
                  </div>

                  {editForm.photos.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 gap-4">
                      {editForm.photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeEditPhoto(index)}
                            className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Description</h3>
                  <p className="text-gray-900 dark:text-white">{incident.description}</p>
                </div>

                {incident.failure_points && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Action Required & Solutions</h3>
                    <div className="space-y-4">
                      {incident.failure_points.split('\n').filter(p => p.trim()).map((point, index) => {
                        const responses = getResponsesForFailurePoint(index);
                        const hasResponses = responses.length > 0;
                        
                        return (
                          <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                            {/* Failure Point */}
                            <div className="flex items-start gap-2 mb-3">
                              <span className="text-blue-600 dark:text-blue-400 mt-1 font-bold">{index + 1}.</span>
                              <span className="text-gray-900 dark:text-white flex-1 font-medium">{point.trim()}</span>
                            </div>

                            {/* Show "waiting for solution" if no responses and user is employee */}
                            {!hasResponses && !isSupplier && (
                              <div className="ml-6 p-3 bg-gray-50 dark:bg-gray-700 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                                <p className="text-sm text-gray-500 dark:text-gray-400 italic flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  Waiting for suggested solution from supplier...
                                </p>
                              </div>
                            )}

                            {/* Responses */}
                            {responses.map((response) => (
                            <div 
                              key={response.id}
                              className={`ml-6 mt-2 p-3 rounded-lg border ${
                                response.status === 'approved' 
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                  : response.status === 'declined'
                                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                  : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              <div className="flex items-start gap-2 mb-2">
                                <Avatar 
                                  userId={response.submitted_by} 
                                  userName={response.submitted_by_name}
                                  avatarUrl={response.submitted_by_avatar}
                                  size="sm" 
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                      {response.submitted_by_name}
                                    </span>
                                    {response.status === 'approved' && (
                                      <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-full flex items-center gap-1">
                                        <Check className="w-3 h-3" /> Approved
                                      </span>
                                    )}
                                    {response.status === 'declined' && (
                                      <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full flex items-center gap-1">
                                        <XCircle className="w-3 h-3" /> Declined
                                      </span>
                                    )}
                                    {response.status === 'pending' && (
                                      <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 rounded-full flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> Pending Review
                                      </span>
                                    )}
                                  </div>
                                  <p className={`text-sm ${
                                    response.status === 'approved'
                                      ? 'text-green-900 dark:text-green-100'
                                      : response.status === 'declined'
                                      ? 'text-red-900 dark:text-red-100 line-through'
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {response.response_text}
                                  </p>
                                  {response.reviewed_by_name && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Reviewed by {response.reviewed_by_name}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Approve/Decline buttons for employees */}
                              {!isSupplier && response.status === 'pending' && (
                                <div className="flex gap-2 mt-2 ml-10">
                                  <button
                                    onClick={() => handleUpdateResponseStatus(response.id, 'approved')}
                                    className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded flex items-center gap-1"
                                  >
                                    <Check className="w-3 h-3" /> Approve
                                  </button>
                                  <button
                                    onClick={() => handleUpdateResponseStatus(response.id, 'declined')}
                                    className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded flex items-center gap-1"
                                  >
                                    <XCircle className="w-3 h-3" /> Decline
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Add Response Input (Suppliers only) */}
                          {isSupplier && (
                            <div className="ml-6 mt-3">
                              {/* Check if there's already an approved solution */}
                              {!responses.some(r => r.status === 'approved') ? (
                                <>
                                  <div className="flex items-start gap-2 mb-1">
                                    <Info className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                      Add your suggested solution here. For discussions, use the chat below.
                                    </p>
                                  </div>
                                  <div className="flex gap-2 mt-2">
                                    <input
                                      type="text"
                                      value={failurePointResponses[index] || ''}
                                      onChange={(e) => setFailurePointResponses(prev => ({
                                        ...prev,
                                        [index]: e.target.value
                                      }))}
                                      placeholder="Suggest a solution..."
                                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                      onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                          handleAddFailurePointResponse(index);
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => handleAddFailurePointResponse(index)}
                                      disabled={submittingResponse[index]}
                                      className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg"
                                    >
                                      {submittingResponse[index] ? 'Adding...' : 'Add Solution'}
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
                                    <CheckCircle className="w-4 h-4" />
                                    Solution approved - no further input needed
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )})}
                    </div>
                  </div>
                )}

                {/* Photos */}
                {incident.photos && incident.photos.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Photos ({incident.photos.length})
                      </h3>
                      <button
                        onClick={() => setShowPhotos(!showPhotos)}
                        className="text-blue-600 dark:text-blue-400 text-sm hover:underline"
                      >
                        {showPhotos ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    
                    {showPhotos && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {incident.photos.map((photo, index) => (
                          <a
                            key={index}
                            href={photo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative"
                          >
                            <img
                              src={photo}
                              alt={`Incident ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg group-hover:opacity-80 transition-opacity"
                            />
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <ImageIcon className="w-8 h-8 text-white" />
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Chat/Messages */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Discussion ({messages.length})
              </h2>
            </div>

            {/* Messages List */}
            <div className="p-6 max-h-96 overflow-y-auto space-y-4">
              {messages.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                  No messages yet. Start the discussion.
                </p>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex gap-3 ${msg.user_id === user?.id ? 'flex-row-reverse' : ''}`}
                  >
                    <Avatar 
                      userId={msg.user_id} 
                      userName={msg.user_name}
                      avatarUrl={msg.user_avatar}
                      size="md"
                      className="flex-shrink-0"
                    />
                    
                    <div className={`flex-1 ${msg.user_id === user?.id ? 'items-end' : 'items-start'} group`}>
                      <div className={`inline-block max-w-md p-3 rounded-lg relative ${
                        msg.user_id === user?.id
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                      }`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{msg.user_name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            msg.user_role === 'Employee'
                              ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                              : 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300'
                          }`}>
                            {msg.user_role}
                          </span>
                          {/* Delete button for admins */}
                          {user?.role === 'admin' && (
                            <button
                              onClick={async () => {
                                if (window.confirm('Delete this comment?')) {
                                  try {
                                    const headers = { Authorization: `Bearer ${token}` };
                                    await axios.delete(`${API}/qc-incidents/${id}/messages/${msg.id}`, { headers });
                                    fetchIncidentData();
                                  } catch (error) {
                                    console.error('Failed to delete message:', error);
                                    alert('Failed to delete comment');
                                  }
                                }
                              }}
                              className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete comment (Admin only)"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <p className="text-sm">{msg.message}</p>
                        
                        {/* Message Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            {msg.attachments.map((att, idx) => (
                              <a
                                key={idx}
                                href={att}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={att}
                                  alt={`Attachment ${idx + 1}`}
                                  className="w-full h-20 object-cover rounded"
                                />
                              </a>
                            ))}
                          </div>
                        )}
                        
                        <p className="text-xs opacity-75 mt-1">
                          {new Date(msg.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-6 border-t border-gray-200 dark:border-gray-700">
              {attachments.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {attachments.map((att, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={att}
                        alt={`Upload ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="absolute -top-2 -right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  disabled={sending || uploading}
                />

                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="message-upload"
                  disabled={uploading || sending}
                />
                <label
                  htmlFor="message-upload"
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                >
                  <Upload className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </label>

                <button
                  type="submit"
                  disabled={sending || uploading || (!newMessage.trim() && attachments.length === 0)}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg flex items-center gap-2"
                >
                  {sending ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Timeline Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Timeline</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Incident Date</p>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(incident.incident_date).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Registered</p>
                  <p className="text-gray-900 dark:text-white">
                    {new Date(incident.date_registered).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Age</p>
                  <p className="text-gray-900 dark:text-white font-semibold">{age} days</p>
                </div>
              </div>

              {incident.solved_date && (
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Solved Date</p>
                    <p className="text-gray-900 dark:text-white">
                      {new Date(incident.solved_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* People Involved */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">People Involved</h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <User className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Registered By</p>
                  <p className="text-gray-900 dark:text-white">{incident.registered_by_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Supplier</p>
                  <p className="text-gray-900 dark:text-white">{incident.supplier_name}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Actions (Employee only) */}
          {!isSupplier && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Actions</h2>
              
              <div className="space-y-3">
                {!incident.sent_to_supplier && isUnsolved && (
                  <button
                    onClick={handleSendToSupplier}
                    className="w-full px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <Mail className="w-5 h-5" />
                    Send to Supplier
                  </button>
                )}

                {incident.sent_to_supplier && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                    <Mail className="w-4 h-4" />
                    <span>Email sent on {new Date(incident.sent_to_supplier_date).toLocaleDateString()}</span>
                  </div>
                )}

                {isUnsolved && (
                  <button
                    onClick={handleMarkAsSolved}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Mark as Solved
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QCIncidentDetail;
